import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Location } from "@/lib/api";

export default function LocationsPage() {
  const qc = useQueryClient();
  const list = useQuery<Location[]>({
    queryKey: ["md", "locations"],
    queryFn: api.listLocations,
  });

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createLocation({
        name,
        natural_key: key || null,
        city: city || null,
        region: region || null,
      }),
    onSuccess: () => {
      setName("");
      setKey("");
      setCity("");
      setRegion("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["md", "locations"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.detail ?? err.message : String(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name) return;
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-900">Locations</h1>
        <p className="text-sm text-slate-600 mt-1">
          Physical or logical place where one or more branches operate.
        </p>
      </header>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Add a location</h2>
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="Region"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Natural key (optional)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={create.isPending || !name}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-md px-3 py-2 disabled:opacity-60"
          >
            {create.isPending ? "Saving…" : "Create"}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
      </section>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Existing locations</h2>
        {list.data && (
          <table className="mt-3 w-full text-sm">
            <thead className="text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">City</th>
                <th className="text-left py-2">Region</th>
                <th className="text-left py-2">Natural key</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((lo) => (
                <tr key={lo.id} className="border-b last:border-none">
                  <td className="py-2">{lo.name}</td>
                  <td className="py-2">{lo.city ?? "—"}</td>
                  <td className="py-2">{lo.region ?? "—"}</td>
                  <td className="py-2 font-mono text-xs">{lo.natural_key ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
